import { ComponentProps, FC, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import cn from 'classnames';

import { VAR_AREA_BOT } from '@mopla/constants';
import { getThemeColor, TMoplaColors } from '@mopla/mui';
import { useBreakpoints } from '@mopla/utils';

interface IOwnerState {
	background?: TMoplaColors;
	borderTop?: boolean;
	withShadow?: boolean;
}

const FooterStyled = styled('div')<{
	ownerState: IOwnerState;
}>(({ theme, ownerState: { background, borderTop = true, withShadow } }) => ({
	width: '100%',
	padding: `16px 16px max(24px, ${VAR_AREA_BOT})`,
	boxSizing: 'border-box',
	...(withShadow && {
		boxShadow:
			'0px 8px 20px 0px rgba(14, 28, 91, 0.08), 0px 2px 8px 0px rgba(14, 28, 91, 0.12)',
	}),

	...(background && { background: getThemeColor(background, theme) }),

	'&.mobile': {
		borderRadius: '8px 8px 0 0',
	},

	'&.aboveMobile': {
		padding: '16px 24px',
		borderTop: borderTop ? `1px solid ${theme.palette.neutral[40]}` : undefined,
	},
}));

interface IFooterProps
	extends Omit<ComponentProps<typeof FooterStyled>, 'ownerState'> {
	enforceMobile?: boolean;
	enforceAboveMobile?: boolean;
	background?: TMoplaColors;
	borderTop?: boolean;
	withShadow?: boolean;
	testId?: string;
}

export const Footer: FC<IFooterProps> = ({
	enforceMobile,
	enforceAboveMobile,
	className,
	background,
	borderTop,
	testId = 'footer',
	withShadow = false,
	...rest
}) => {
	const { isMobile, isAboveMobile } = useBreakpoints({
		enforceMobile,
		enforceAboveMobile,
	});

	const ownerState = useMemo<IOwnerState>(
		() => ({ background, borderTop, withShadow }),
		[background, borderTop, withShadow]
	);

	return (
		<FooterStyled
			{...rest}
			data-testid={testId}
			ownerState={ownerState}
			className={cn(
				className,
				isMobile && 'mobile',
				isAboveMobile && 'aboveMobile'
			)}
		/>
	);
};
